import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Upload, Trash2, Image as ImageIcon,
  Layers, Loader2, X, ChevronDown, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

import { supabase } from "@/integrations/supabase/client";

// ── Types ──────────────────────────────────────────────

export interface ShapeOverlay {
  id: string;
  type: "image";
  x: number; // normalized center 0-1
  y: number;
  widthPx: number;
  heightPx: number;
  aspectRatio?: number;
  opacity: number; // 0-1
  imageSrc?: string;
  // Keep legacy fields so saved data doesn't break
  fillColor?: string;
  borderRadius?: number;
  text?: string;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: string;
  fontStyle?: string;
  textColor?: string;
  textAlign?: string;
}

interface ImageAsset {
  id: string;
  file_name: string;
  storage_path: string;
  publicUrl: string;
}

// ── Component ──────────────────────────────────────────

interface CreativeToolkitProps {
  containerRef: React.RefObject<HTMLDivElement>;
  overlays: ShapeOverlay[];
  setOverlays: React.Dispatch<React.SetStateAction<ShapeOverlay[]>>;
  selectedOverlayId: string | null;
  setSelectedOverlayId: (id: string | null) => void;
  workspaceId?: string;
  userId?: string;
  onFlatten?: (dataUrl: string) => void;
  currentImageUrl: string;
  embedded?: boolean;
}

export function CreativeToolkit({
  containerRef,
  overlays,
  setOverlays,
  selectedOverlayId,
  setSelectedOverlayId,
  workspaceId,
  userId,
  onFlatten,
  currentImageUrl,
  embedded,
}: CreativeToolkitProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageLibrary, setImageLibrary] = useState<ImageAsset[]>([]);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(["tools"]));

  const selected = overlays.find((o) => o.id === selectedOverlayId) || null;

  // Load image library
  useEffect(() => {
    if (!workspaceId) return;
    (async () => {
      const { data, error } = await supabase
        .from("overlay_assets")
        .select("id, file_name, storage_path")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) return;
      const assets: ImageAsset[] = (data || []).map((row: any) => {
        const { data: urlData } = supabase.storage.from("overlays").getPublicUrl(row.storage_path);
        return { ...row, publicUrl: urlData.publicUrl };
      });
      setImageLibrary(assets);
    })();
  }, [workspaceId]);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ── Add image overlay ──
  const addImageFromUrl = (url: string) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const containerW = containerRef.current?.offsetWidth || 400;
      const initialW = containerW * 0.25;
      const ar = (img.naturalWidth || 200) / (img.naturalHeight || 200);
      const newOverlay: ShapeOverlay = {
        id: crypto.randomUUID(),
        type: "image",
        x: 0.5,
        y: 0.5,
        widthPx: initialW,
        heightPx: initialW / ar,
        aspectRatio: ar,
        opacity: 1,
        imageSrc: url,
      };
      setOverlays((prev) => [...prev, newOverlay]);
      setSelectedOverlayId(newOverlay.id);
    };
    img.src = url;
  };

  const uploadImage = async (file: File): Promise<ImageAsset | null> => {
    if (!workspaceId || !userId) return null;
    setIsUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const storagePath = `${userId}/${workspaceId}/${crypto.randomUUID()}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from("overlays").upload(storagePath, file, { contentType: file.type });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from("overlays").getPublicUrl(storagePath);
      const { data: dbRow, error: dbErr } = await supabase
        .from("overlay_assets")
        .insert({ workspace_id: workspaceId, user_id: userId, file_name: file.name, storage_path: storagePath, mime_type: file.type } as any)
        .select("id, file_name, storage_path")
        .single();
      if (dbErr) throw dbErr;
      const asset: ImageAsset = { id: dbRow.id, file_name: dbRow.file_name, storage_path: dbRow.storage_path, publicUrl: urlData.publicUrl };
      setImageLibrary((prev) => [asset, ...prev]);
      return asset;
    } catch (err) {
      console.error("Upload failed:", err);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (workspaceId && userId) {
      const asset = await uploadImage(file);
      if (asset) addImageFromUrl(asset.publicUrl);
    } else {
      addImageFromUrl(URL.createObjectURL(file));
    }
  };

  const deleteLibraryAsset = async (asset: ImageAsset) => {
    await supabase.storage.from("overlays").remove([asset.storage_path]);
    await supabase.from("overlay_assets").delete().eq("id", asset.id);
    setImageLibrary((prev) => prev.filter((a) => a.id !== asset.id));
  };

  const updateSelected = (patch: Partial<ShapeOverlay>) => {
    if (!selectedOverlayId) return;
    setOverlays((prev) =>
      prev.map((o) => (o.id === selectedOverlayId ? { ...o, ...patch } : o))
    );
  };

  const deleteOverlay = (id: string) => {
    setOverlays((prev) => prev.filter((o) => o.id !== id));
    if (selectedOverlayId === id) setSelectedOverlayId(null);
  };

  // ── Render ──────────────────────────────────────────

  const content = (
    <>
      {/* Header */}
      <div className="p-3 border-b border-border">
        <h3 className="font-display font-semibold text-sm flex items-center gap-1.5">
          <Layers className="w-3.5 h-3.5 text-primary" />
          Image Overlay
        </h3>
      </div>

      {/* Upload section */}
      <div className="border-b border-border">
        <button
          className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => toggleSection("tools")}
        >
          Upload Image
          {expandedSections.has("tools") ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
        {expandedSections.has("tools") && (
          <div className="px-3 pb-3 space-y-2">
            <Button variant="outline" size="sm" className="w-full justify-start text-xs" onClick={() => setShowImagePicker(!showImagePicker)}>
              <ImageIcon className="w-3.5 h-3.5 mr-2" />
              Add Image
            </Button>

            {showImagePicker && (
              <div className="rounded-lg border border-border bg-muted/30 p-2 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Image Library</span>
                  <Button
                    variant="outline" size="sm" className="h-6 text-[10px] px-2"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                    Upload
                  </Button>
                </div>
                {imageLibrary.length === 0 ? (
                  <p className="text-[10px] text-muted-foreground text-center py-2">No images yet</p>
                ) : (
                  <div className="grid grid-cols-4 gap-1">
                    {imageLibrary.map((asset) => (
                      <div key={asset.id} className="relative group/asset">
                        <button
                          className="w-full aspect-square rounded border border-border bg-muted/50 hover:border-primary/50 transition-colors overflow-hidden flex items-center justify-center p-0.5"
                          onClick={() => addImageFromUrl(asset.publicUrl)}
                        >
                          <img src={asset.publicUrl} alt={asset.file_name} className="w-full h-full object-contain" crossOrigin="anonymous" />
                        </button>
                        <button
                          className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover/asset:opacity-100 transition-opacity"
                          onClick={() => deleteLibraryAsset(asset)}
                        >
                          <X className="w-2 h-2" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Layers list */}
      {overlays.length > 0 && (
        <div className="border-b border-border">
          <button
            className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => toggleSection("layers")}
          >
            Layers ({overlays.length})
            {expandedSections.has("layers") ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
          {expandedSections.has("layers") && (
            <div className="px-3 pb-2 space-y-1">
              {overlays.map((ov) => (
                <div
                  key={ov.id}
                  className={cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded-md text-xs cursor-pointer transition-colors",
                    selectedOverlayId === ov.id
                      ? "bg-primary/10 border border-primary/30 text-foreground"
                      : "hover:bg-muted text-muted-foreground"
                  )}
                  onClick={() => setSelectedOverlayId(ov.id)}
                >
                  <ImageIcon className="w-3 h-3 flex-shrink-0" />
                  <span className="flex-1 truncate">Image</span>
                  <button
                    className="w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
                    onClick={(e) => { e.stopPropagation(); deleteOverlay(ov.id); }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Selected element properties — opacity only */}
      {selected && (
        <div className="px-3 py-3">
          <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
            Opacity ({Math.round(selected.opacity * 100)}%)
          </label>
          <Slider
            value={[selected.opacity * 100]}
            onValueChange={([v]) => updateSelected({ opacity: v / 100 })}
            min={10}
            max={100}
            step={5}
            className="w-full"
          />
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileUpload}
      />
    </>
  );

  if (embedded) return content;

  return (
    <div className="w-64 bg-card border border-border rounded-xl shadow-lg overflow-hidden flex flex-col max-h-[80vh]">
      {content}
    </div>
  );
}

// ── Canvas overlay renderer ──────────────────────────

interface OverlayCanvasProps {
  overlays: ShapeOverlay[];
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  setOverlays: React.Dispatch<React.SetStateAction<ShapeOverlay[]>>;
  containerRef: React.RefObject<HTMLDivElement>;
}

export function OverlayCanvas({ overlays, selectedId, setSelectedId, setOverlays, containerRef }: OverlayCanvasProps) {
  const dragRef = useRef<{ id: string; startX: number; startY: number; origX: number; origY: number; mode: "drag" | "resize"; origW: number; origH: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent, ov: ShapeOverlay, mode: "drag" | "resize") => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(ov.id);
    dragRef.current = { id: ov.id, startX: e.clientX, startY: e.clientY, origX: ov.x, origY: ov.y, mode, origW: ov.widthPx, origH: ov.heightPx };

    const handleMouseMove = (ev: MouseEvent) => {
      if (!dragRef.current || !containerRef.current) return;
      const d = dragRef.current;
      const container = containerRef.current;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;

      if (d.mode === "drag") {
        setOverlays((prev) =>
          prev.map((o) =>
            o.id === d.id
              ? { ...o, x: d.origX + dx / container.offsetWidth, y: d.origY + dy / container.offsetHeight }
              : o
          )
        );
      } else {
        const newW = Math.max(30, d.origW + dx);
        const ov = overlays.find((o) => o.id === d.id);
        const ar = ov?.aspectRatio || 1;
        const newH = newW / ar;
        // x/y are the overlay's *centre*, so growing the box alone expands it
        // in both directions and the bottom-right handle only travels half as
        // far as the cursor — it slides out from under the pointer mid-drag.
        // Shift the centre by half the growth instead, which pins the top-left
        // corner and makes the handle track the cursor 1:1.
        setOverlays((prev) =>
          prev.map((o) =>
            o.id === d.id
              ? {
                  ...o,
                  widthPx: newW,
                  heightPx: newH,
                  x: d.origX + (newW - d.origW) / 2 / container.offsetWidth,
                  y: d.origY + (newH - d.origH) / 2 / container.offsetHeight,
                }
              : o
          )
        );
      }
    };

    const handleMouseUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);

      // stopPropagation on mousedown does not stop the *click* that follows.
      // When a drag ends off the overlay, that click is dispatched on the
      // nearest common ancestor — the canvas background, whose handler clears
      // the selection and unmounts the resize handle with it. That is why a
      // logo could only be scaled once: after the first resize the handle was
      // gone, and re-selecting deselected again on the very same click, so
      // only dragging still worked.
      // Capture phase, so it runs before the background handler; `once` so it
      // retires after the single click this interaction produces. No timer
      // cleanup here on purpose — a setTimeout(0) can run before the click is
      // dispatched and would disarm the guard it is meant to protect.
      window.addEventListener("click", (ev: MouseEvent) => ev.stopPropagation(), {
        capture: true,
        once: true,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <>
      {overlays.map((ov) => (
        <div
          key={ov.id}
          className={cn(
            "absolute cursor-move",
            selectedId === ov.id && "ring-2 ring-primary ring-offset-1"
          )}
          style={{
            left: `${ov.x * 100}%`,
            top: `${ov.y * 100}%`,
            width: `${ov.widthPx}px`,
            height: `${ov.heightPx}px`,
            transform: "translate(-50%, -50%)",
            opacity: ov.opacity,
            zIndex: 10,
          }}
          onMouseDown={(e) => handleMouseDown(e, ov, "drag")}
        >
          {ov.imageSrc && (
            <img
              src={ov.imageSrc}
              alt="Overlay"
              className="w-full h-full object-contain pointer-events-none"
              draggable={false}
              crossOrigin="anonymous"
            />
          )}

          {/* Resize handle */}
          {selectedId === ov.id && (
            <div
              className="absolute bottom-0 right-0 w-4 h-4 bg-primary border-2 border-primary-foreground rounded-sm cursor-nwse-resize"
              style={{ transform: "translate(50%, 50%)" }}
              onMouseDown={(e) => handleMouseDown(e, ov, "resize")}
            />
          )}
        </div>
      ))}
    </>
  );
}

// ── Exported flatten utility for download ──

export function flattenOverlaysToCanvas(
  overlays: ShapeOverlay[],
  container: HTMLDivElement,
  currentImageUrl: string,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const imgEl = container.querySelector("img:first-of-type") as HTMLImageElement;
    if (!imgEl) { reject(new Error("No image element found")); return; }

    const renderedW = imgEl.clientWidth;
    const renderedH = imgEl.clientHeight;

    const baseImg = new Image();
    baseImg.crossOrigin = "anonymous";
    baseImg.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = baseImg.naturalWidth;
      canvas.height = baseImg.naturalHeight;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(baseImg, 0, 0);

      const scaleX = baseImg.naturalWidth / renderedW;
      const scaleY = baseImg.naturalHeight / renderedH;
      const imgOffsetX = (container.offsetWidth - renderedW) / 2;
      const imgOffsetY = (container.offsetHeight - renderedH) / 2;

      const getDrawCoords = (ov: ShapeOverlay) => {
        const leftInContainer = ov.x * container.offsetWidth - ov.widthPx / 2;
        const topInContainer = ov.y * container.offsetHeight - ov.heightPx / 2;
        return {
          drawX: (leftInContainer - imgOffsetX) * scaleX,
          drawY: (topInContainer - imgOffsetY) * scaleY,
          drawW: ov.widthPx * scaleX,
          drawH: ov.heightPx * scaleY,
        };
      };

      // Draw image overlays
      const imageOverlays = overlays.filter((o) => o.imageSrc);
      if (imageOverlays.length === 0) {
        resolve(canvas.toDataURL("image/png"));
        return;
      }

      let completed = 0;
      for (const ov of imageOverlays) {
        const ovImg = new Image();
        ovImg.crossOrigin = "anonymous";
        ovImg.onload = () => {
          const { drawX, drawY, drawW, drawH } = getDrawCoords(ov);
          ctx.save();
          ctx.globalAlpha = ov.opacity;
          ctx.drawImage(ovImg, drawX, drawY, drawW, drawH);
          ctx.restore();
          completed++;
          if (completed === imageOverlays.length) resolve(canvas.toDataURL("image/png"));
        };
        ovImg.onerror = () => {
          completed++;
          if (completed === imageOverlays.length) resolve(canvas.toDataURL("image/png"));
        };
        ovImg.src = ov.imageSrc!;
      }
    };
    baseImg.onerror = () => reject(new Error("Failed to load base image"));
    baseImg.src = currentImageUrl;
  });
}
